// ============================================================================
// SYNCHRONIZATION MODELS
// ============================================================================

export interface SynchronizationRecord {
  adjustId?: number;
  adjustCode: string;
  warehouseCode: string;
  warehouseName: string;
  status: 'pending' | 'success' | 'failed' | 'approved' | 'rejected';
  lastUpdateName: string;
  lastUpdateDate: string;
  remarks?: string;
  adjustDetailsReqDTOList?: SynchronizationDetail[];
}

export interface SynchronizationItem {
  id?: number;
  adjustCode: string;
  warehouseCode: string;
  warehouseName: string;
  status?: string;
  lastUpdateName?: string;
  lastUpdateDate?: string;
  remarks: string;
  warehouseId?: number;
  adjustDetailsReqDTOList?: SynchronizationDetail[];
  createDate?: string;
}

export interface SynchronizationDetail {
  id?: number;
  serialNumber?: number;
  categoryName: string;
  spuCode: string;
  spuName: string;
  skuCode: string;
  skuName?: string;
  adjustmentType?: 'INCREASE' | 'DECREASE';
  adjustmentQuantity: number | null;
  planNum?: number | null;
  realityNum?: number | null;
  note?: string;
  batchId?: string;
  /** True when serial profile requires a lot for this SKU. */
  lotRequired?: boolean;
  fefoSuggestedBatchId?: string;
  overrideReason?: string;
  type?: 'INCREASE' | 'DECREASE'; // Legacy field
  num?: number; // Legacy field
}

export interface SynchronizationFormData {
  warehouseId?: number;
  warehouseCode?: string;
  warehouseName?: string;
  logicalDataSyncNumber?: string;
  remarks?: string;
  adjustDetailsReqDTOList?: SynchronizationDetail[];
}

export interface SynchronizationSearchParams {
  adjustCode?: string;
  warehouseCode?: string;
  warehouseName?: string;
  status?: string[];
  startTime?: string;
  endTime?: string;
  syncTime?: any;
  page?: number;
  size?: number;
  sortBy?: string;
  ascending?: boolean;
}

export interface SynchronizationListResponse {
  data: SynchronizationItem[];
  totalRecords: number;
  loading: boolean;
}

// ============================================================================
// WAREHOUSE MODELS
// ============================================================================

export interface Warehouse {
  id: number;
  code: string;
  name: string;
  type?: string;
  status?: number;
  address?: string;
  contactPerson?: string;
  contactPhone?: string;
  remarks?: string;
  serialNumber?: number;
}

export interface WarehouseSearchParams {
  code?: string;
  name?: string;
  type?: string;
  status?: number;
  page?: number;
  size?: number;
}

export interface WarehouseListResponse {
  content: Warehouse[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
  numberOfElements: number;
  empty: boolean;
}

export interface WarehouseInfo {
  id: number;
  code: string;
  name: string;
}

// ============================================================================
// PRODUCT MODELS
// ============================================================================

export interface Product {
  id: number;
  serialNumber?: number;
  categoryName: string;
  spuCode: string;
  spuName: string;
  skuCode: string;
  skuName: string;
  productType?: string;
}

export interface ProductSearchParams {
  productName?: string;
  productCode?: string;
  skuCode?: string;
  page?: number;
  size?: number;
}

export interface ProductListResponse {
  content: Product[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  empty: boolean;
}

export interface ProductInfo {
  id: number;
  categoryName: string;
  spuCode: string;
  spuName: string;
  skuCode: string;
}

// ============================================================================
// COMMON MODELS
// ============================================================================

export interface StockAdjustType {
  label: string;
  value: 'INCREASE' | 'DECREASE';
}

export interface SynchronizationOperationResult {
  success: boolean;
  message: string;
  type: 'create' | 'update' | 'approve';
}

export type SynchronizationAction =
  | 'view'
  | 'edit'
  | 'delete'
  | 'approve'
  | 'add';

export interface SynchronizationFormActionEvent {
  action: string;
  data?: any;
}

// ============================================================================
// CONSTANTS
// ============================================================================

export const STOCK_ADJUST_TYPES: StockAdjustType[] = [
  { label: 'Increase', value: 'INCREASE' },
  { label: 'Decrease', value: 'DECREASE' },
];

export const SYNCHRONIZATION_STATUS_OPTIONS = [
  { label: 'Waiting', value: 'wait' },
  { label: 'Approved', value: 'success' },
  { label: 'Rejected', value: 'fail' },
];
